'use client';

import { Header } from "@/components/header/Header";
import InnerHero from "@/components/InnerHero/InnerHero";
import { AnimatePresence, motion } from 'framer-motion';
import { useBuilderStore } from '@/store/builderStore';
import Sidebar from '@/app/components/Builder/Sidebar';
import Stepper from '@/app/components/Builder/Stepper';
import ProductDescription from '@/app/components/Builder/ProductDescription';
import MaterialSelector from '@/app/components/Builder/MaterialSelector';
import UploadReference from '@/app/components/Builder/UploadReference';
import Preview from '@/app/components/Builder/Preview';
import Review from '@/app/components/Builder/Review';
import Success from '@/app/components/Builder/Success';
import AIPreviewPanel from '@/app/components/Builder/AIPreviewPanel';
import { Footer } from "@/components/Footer/Footer";

const stepLabels: Record<number, string> = {
  1: 'Describe Product',
  2: 'Material & Finish',
  3: 'Upload Reference',
  4: 'AI Preview',
  5: 'Review & Submit',
};

export default function AIProductBuilderPage() {
  const { currentStep, isSuccess } = useBuilderStore();

  return (
    <>
      <Header />
      <InnerHero pageKey="ai-builder" />
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50/20">
        {/* Top navbar */}


        {/* Hero banner */}
        {!isSuccess && (
          <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-b border-slate-700">
            <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((step) => (
                    <div key={step} className="flex items-center gap-1">
                      <div
                        className={`w-2 h-2 rounded-full transition-all duration-300 ${step < currentStep
                            ? 'bg-emerald-400'
                            : step === currentStep
                              ? 'bg-amber-400 scale-125'
                              : 'bg-slate-600'
                          }`}
                      />
                      {step < 5 && (
                        <div
                          className={`w-8 h-px transition-colors duration-300 ${step < currentStep ? 'bg-emerald-400' : 'bg-slate-600'
                            }`}
                        />
                      )}
                    </div>
                  ))}
                </div>
                <span className="text-slate-300 text-xs font-medium hidden sm:block">
                  Step {currentStep} of 5 — {stepLabels[currentStep]}
                </span>
              </div>
              <div className="flex items-center gap-4 text-xs text-slate-400">
                <span>🔒 Secure & Encrypted</span>
                <span className="hidden sm:block">⚡ AI-Powered</span>
                <span className="hidden md:block">🌍 Global Export</span>
              </div>
            </div>
          </div>
        )}

        {/* Main content */}
        <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {isSuccess ? (
            <div className="max-w-2xl mx-auto">
              <div className="bg-white rounded-3xl shadow-2xl shadow-slate-200/60 border border-slate-100 overflow-hidden">
                <div className="h-2 bg-gradient-to-r from-emerald-400 via-emerald-500 to-emerald-600" />
                <Success />
              </div>
            </div>
          ) : (
            <div className="flex gap-6 xl:gap-8 items-start">
              {/* Left Sidebar - Steps */}
              <Sidebar />

              {/* Center - Main builder form */}
              <div className="flex-1 min-w-0">
                {/* Card header with step number */}
                <div className="mb-4 flex items-center gap-3">
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-800 rounded-lg">
                    <span className="text-amber-400 text-xs font-black">
                      {String(currentStep).padStart(2, '0')}.
                    </span>
                    <span className="text-white text-xs font-bold uppercase tracking-wider">
                      {stepLabels[currentStep]}
                    </span>
                  </div>
                </div>

                <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 overflow-hidden">
                  {/* Progress bar */}
                  <div className="h-1 bg-slate-100">
                    <motion.div
                      className="h-full bg-gradient-to-r from-amber-400 to-amber-600"
                      initial={{ width: '0%' }}
                      animate={{ width: `${((currentStep - 1) / 4) * 100}%` }}
                      transition={{ duration: 0.6, ease: 'easeOut' }}
                    />
                  </div>

                  <div className="p-6 sm:p-8">
                    {/* Mobile stepper */}
                    <Stepper />

                    {/* Step content with animation */}
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={currentStep}
                        initial={{ opacity: 0, x: 16 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -16 }}
                        transition={{ duration: 0.3, ease: 'easeOut' }}
                      >
                        {currentStep === 1 && <ProductDescription />}
                        {currentStep === 2 && <MaterialSelector />}
                        {currentStep === 3 && <UploadReference />}
                        {currentStep === 4 && <Preview />}
                        {currentStep === 5 && <Review />}
                      </motion.div>
                    </AnimatePresence>
                  </div>
                </div>

                {/* Footer note */}
                <p className="text-center text-xs text-slate-400 mt-4 flex items-center justify-center gap-2">
                  <span>🔒</span>
                  <span>
                    Your information is secure and encrypted. We never share your data with third
                    parties.
                  </span>
                </p>
              </div>

              {/* Right Panel - AI Preview (always visible on xl screens) */}
              <AIPreviewPanel />
            </div>
          )}
        </main>
      </div>
      <Footer />
    </>
  );
}
